import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { mcpSummary } from "../api/Api";
import type { McpSummaryData } from "../model/Model";
import { useUser } from "./UserContextUtils";
import {
  MCP_POLL_MS,
  MCP_URGENT_MS,
  McpStatusContext,
} from "./McpStatusContextUtils";

/**
 * Keeps the account menu's pending-approval badge honest.
 *
 * A queued change expires on its own, and the user only finds out it was
 * waiting if something outside /connections tells them. That is the entire job
 * here, which is why it costs one counts-only request rather than the three
 * list requests the connections page makes.
 *
 * The polling loop stops itself for the overwhelming majority of accounts:
 * with no live connection there is nothing that could ever queue a change, so
 * after the first response the interval is not scheduled at all. Connecting an
 * assistant happens on /connections, which calls `refresh()` and restarts it.
 */
export function McpStatusProvider({ children }: { children: ReactNode }) {
  const { user } = useUser();
  const [summary, setSummary] = useState<McpSummaryData | null>(null);

  // Read inside the interval callback so the effect does not re-subscribe every
  // time the counts change.
  const summaryRef = useRef<McpSummaryData | null>(null);
  const inflightRef = useRef<Promise<void> | null>(null);
  const lastFetchRef = useRef(0);

  const refresh = useCallback(async () => {
    if (inflightRef.current) return inflightRef.current;
    lastFetchRef.current = Date.now();
    const run = (async () => {
      try {
        const res = await mcpSummary();
        if (!res.ok) return;
        const body = (await res.json()) as { data: McpSummaryData };
        summaryRef.current = body.data;
        setSummary(body.data);
      } catch {
        // A badge is never worth surfacing an error for. The connections page
        // reports its own failures; this one stays quiet and tries again.
      } finally {
        inflightRef.current = null;
      }
    })();
    inflightRef.current = run;
    return run;
  }, []);

  useEffect(() => {
    if (!user) {
      summaryRef.current = null;
      setSummary(null);
      return;
    }

    let cancelled = false;
    void refresh();

    const tick = () => {
      if (cancelled) return;
      if (document.visibilityState !== "visible") return;
      // Nothing can queue a change without a live connection.
      if (summaryRef.current && summaryRef.current.connections === 0) return;
      void refresh();
    };

    const interval = window.setInterval(tick, MCP_POLL_MS);

    // Coming back to the tab is the moment the count is most likely stale and
    // most likely about to be acted on.
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (Date.now() - lastFetchRef.current < 15_000) return;
      tick();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user, refresh]);

  const pending = summary?.pending ?? 0;
  const urgent =
    pending > 0 &&
    !!summary?.next_expiry &&
    new Date(summary.next_expiry).getTime() - Date.now() < MCP_URGENT_MS;

  return (
    <McpStatusContext.Provider value={{ summary, pending, urgent, refresh }}>
      {children}
    </McpStatusContext.Provider>
  );
}
